import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Icon, cn } from "@nexus/ui";
import type {
  PreflightReportAsset,
  PreflightReportDetail,
} from "../../types/preflight.types";
import { assetToText } from "../../lib/export-report";

export interface AssetCopyTextButtonProps {
  /** Parent report — supplies the report-level context for the per-asset text. */
  report: PreflightReportDetail;
  asset: PreflightReportAsset;
  className?: string;
}

/** Copies a single creative's plain-text report (same content as the Text export) to the clipboard. */
export function AssetCopyTextButton({
  report,
  asset,
  className,
}: AssetCopyTextButtonProps) {
  const { t } = useTranslation();

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(assetToText(report, asset));
      toast.success(t("preflight.toast.copySuccess"));
    } catch {
      toast.error(t("preflight.toast.copyError"));
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn("shrink-0 gap-1.5", className)}
      aria-label={t("preflight.export.copyTextAria")}
      onClick={() => void handleCopy()}
    >
      <Icon name="copy" size="sm" color="inherit" />
      {t("preflight.export.copyText")}
    </Button>
  );
}
